// Licensed to the Apache Software Foundation (ASF) under one
// or more contributor license agreements.  See the NOTICE file
// distributed with this work for additional information
// regarding copyright ownership.  The ASF licenses this file
// to you under the Apache License, Version 2.0 (the
// "License"); you may not use this file except in compliance
// with the License.  You may obtain a copy of the License at
//
//   http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing,
// software distributed under the License is distributed on an
// "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
// KIND, either express or implied.  See the License for the
// specific language governing permissions and limitations
// under the License.

<template>
  <a-spin :spinning="loading">
    <a-form
      class="form"
      layout="vertical"
      :ref="formRef"
      :model="form"
      :rules="rules"
      @finish="handleEvaluate"
      v-ctrl-enter="handleEvaluate">
      <a-form-item ref="usageId" name="usageId">
        <template #label>
          <tooltip-label :title="$t('label.quota.evaluate.activationrule.usageid')" :tooltip="$t('label.quota.evaluate.activationrule.usageid.tooltip')"/>
        </template>
        <a-input-number
          class="full-width-input"
          v-focus="true"
          v-model:value="form.usageId"
          :min="1"
          :placeholder="$t('label.quota.evaluate.activationrule.usageid.placeholder')" />
      </a-form-item>
      <a-form-item ref="activationRule" name="activationRule">
        <template #label>
          <tooltip-label :title="$t('label.quota.tariff.activationrule')" :tooltip="$t('label.quota.evaluate.activationrule.tooltip')"/>
        </template>
        <a-textarea
          v-model:value="form.activationRule"
          :placeholder="$t('placeholder.quota.tariff.activationrule')"
          :max-length="65535"
          :auto-size="{ minRows: 6, maxRows: 18 }" />
      </a-form-item>
      <div class="action-button">
        <a-button @click="closeModal">{{ $t('label.cancel') }}</a-button>
        <a-button type="primary" ref="submit" @click="handleEvaluate">{{ $t('label.quota.evaluate.activationrule.run') }}</a-button>
      </div>
    </a-form>

    <a-divider v-if="evaluationResult || evaluationError" />

    <a-alert
      v-if="evaluationError"
      type="error"
      show-icon
      :message="$t('label.quota.evaluate.activationrule.error')"
      :description="evaluationError"
      style="margin-bottom: 12px" />

    <div v-if="evaluationResult">
      <a-descriptions :title="$t('label.quota.evaluate.activationrule.result')" bordered size="small" :column="1">
        <a-descriptions-item :label="$t('label.quota.evaluate.activationrule.apply')">
          <a-tag :color="evaluationResult.apply ? 'green' : 'red'">
            {{ evaluationResult.apply ? $t('label.yes') : $t('label.no') }}
          </a-tag>
        </a-descriptions-item>
        <a-descriptions-item :label="$t('label.quota.evaluate.activationrule.result.value')">
          <pre class="result-block">{{ evaluationResult.result }}</pre>
        </a-descriptions-item>
        <a-descriptions-item :label="$t('label.quota.evaluate.activationrule.result.type')">
          {{ evaluationResult.resulttype }}
        </a-descriptions-item>
        <a-descriptions-item :label="$t('label.quota.evaluate.activationrule.multiplier')">
          {{ evaluationResult.multiplier }}
        </a-descriptions-item>
        <a-descriptions-item :label="$t('label.usagetype')" v-if="evaluationResult.usagetypename">
          {{ evaluationResult.usagetypename }} ({{ evaluationResult.usagetype }})
        </a-descriptions-item>
      </a-descriptions>

      <a-collapse v-if="evaluationResult.presetvariables" style="margin-top: 12px">
        <a-collapse-panel key="presetvariables" :header="$t('label.quota.evaluate.activationrule.presetvariables')">
          <pre class="result-block">{{ evaluationResult.presetvariables }}</pre>
        </a-collapse-panel>
      </a-collapse>
    </div>
  </a-spin>
</template>

<script>
import { postAPI } from '@/api'
import { ref, reactive, toRaw } from 'vue'
import TooltipLabel from '@/components/widgets/TooltipLabel'

export default {
  name: 'EvaluateQuotaActivationRule',
  components: {
    TooltipLabel
  },
  data: () => ({
    loading: false,
    evaluationResult: null,
    evaluationError: null
  }),
  created () {
    this.initForm()
  },
  methods: {
    initForm () {
      this.formRef = ref()
      this.form = reactive({
        usageId: null,
        activationRule: ''
      })
      this.rules = reactive({
        usageId: [{ required: true, message: this.$t('label.quota.evaluate.activationrule.usageid.required') }],
        activationRule: [{ required: true, message: this.$t('label.quota.evaluate.activationrule.rule.required') }]
      })
    },
    closeModal () {
      this.$emit('close-action')
    },
    handleEvaluate (e) {
      if (e && e.preventDefault) {
        e.preventDefault()
      }
      if (this.loading) return

      this.formRef.value.validate().then(() => {
        const formRaw = toRaw(this.form)
        this.loading = true
        this.evaluationResult = null
        this.evaluationError = null

        postAPI('billingQuotaEvaluateActivationRule', {
          activationrule: formRaw.activationRule,
          usageid: formRaw.usageId
        }).then(response => {
          const payload = response.billingquotaevaluateactivationruleresponse &&
            (response.billingquotaevaluateactivationruleresponse.payload ||
             response.billingquotaevaluateactivationruleresponse)
          if (payload && payload.error) {
            this.evaluationError = payload.error
            this.evaluationResult = payload
          } else {
            this.evaluationResult = payload
          }
        }).catch(error => {
          this.evaluationError = (error.response && error.response.headers && error.response.headers['x-description']) ||
            (error.message ? error.message : String(error))
        }).finally(() => {
          this.loading = false
        })
      }).catch(() => {})
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/style/objects/form.scss';

.result-block {
  background: rgba(0, 0, 0, 0.04);
  border-radius: 4px;
  padding: 8px;
  max-height: 320px;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-word;
  margin: 0;
}
</style>
